/** "创意候选" / Creative Candidate RESULT node
 * (VISTOERA_CANVAS_PROJECT_START_CREATIVE_BRIEF_AND_IDEATION_BRANCHING_
 * NODE_V1 §31/§32). One AI-generated creative direction — its OWN
 * independent Canvas node (never a card stacked inside `project_start`,
 * spec §31 "不要把三条候选永远塞在 Project Start card内部... Canvas
 * branching才是核心价值"). Deliberately no media/result-URL model (spec
 * §32 "V1不需要真的生成候选视觉缩略图") — its content IS its own text
 * fields (title/premise/hook/tone/estimated duration/tags). */

import { CheckOutlined, FileDoneOutlined, LoadingOutlined, StopOutlined, ThunderboltOutlined } from '@ant-design/icons'
import { Handle, Position, type NodeProps } from '@xyflow/react'
import { Tooltip } from 'antd'
import { memo, useRef, useState, type DragEvent as ReactDragEvent, type MouseEvent as ReactMouseEvent } from 'react'
import CanvasActionPopover from '../CanvasActionPopover'
import { useNodeDefinition } from '../nodeRegistry'
import type { CreativeContextDragPayload, FlowNodeData } from '../types'

const NODE_WIDTH = 250

import { PCV_CREATIVE_CONTEXT_DND_MIME, vistoeraDndProps } from '../canvasDragContract'

/** VISTOERA_CANVAS_SCRIPT_GENERATION_NODE_AND_CREATIVE_ASSET_CONTEXT_
 * LINKING_V1 §3 — Drag a `creative_candidate` node's own current result
 * out onto a `script_generation` node's drop target. Same "custom MIME,
 * never text/plain" convention as `InputMaterialsRow`'s
 * `ASSET_MENTION_DND_TYPE`. */
export const CREATIVE_CONTEXT_DND_TYPE = PCV_CREATIVE_CONTEXT_DND_MIME

function formatDuration(seconds: number | null | undefined): string | null {
  if (!seconds || seconds <= 0) return null
  if (seconds < 60) return `${Math.round(seconds)}秒`
  const minutes = seconds / 60
  return `${minutes % 1 === 0 ? minutes : minutes.toFixed(1)}分钟`
}

function CreativeCandidateNodeImpl({ id, data, selected }: NodeProps) {
  const nodeData = data as unknown as FlowNodeData
  const def = useNodeDefinition(nodeData.nodeType)
  const fields = nodeData.fields || {}

  const title = String(fields.title ?? '')
  const premise = String(fields.premise ?? '')
  const hook = String(fields.hook ?? '')
  const tone = String(fields.tone ?? '')
  const productRole = (fields.productRole as string | null) ?? null
  const characterRole = (fields.characterRole as string | null) ?? null
  const estimatedDuration = (fields.estimatedDuration as number | null) ?? null
  const tags = Array.isArray(fields.tags) ? (fields.tags as string[]) : []
  const selectedCandidate = Boolean(fields.selected)
  const rejected = Boolean(fields.rejected)
  const stale = Boolean(fields.stale)
  const generationDepth = (fields.generationDepth as number) || 0
  const expandRunStatus = (fields.expandRunStatus as string | null) ?? null
  const expandRunErrorMessage = (fields.expandRunErrorMessage as string | null) ?? null
  const expandBusy = expandRunStatus === 'SUBMITTING' || expandRunStatus === 'QUEUED' || expandRunStatus === 'GENERATING'

  const [expandPopoverOpen, setExpandPopoverOpen] = useState(false)
  const [expandInstruction, setExpandInstruction] = useState('')
  const expandBtnRef = useRef<HTMLButtonElement>(null)

  const durationLabel = formatDuration(estimatedDuration)
  const sceneEstimateTag = tags.find((t) => /场景|scene/i.test(t))

  const handleSelect = (e: ReactMouseEvent) => {
    e.stopPropagation()
    nodeData.onSetCreativeCandidateState?.(id, { selected: !selectedCandidate, rejected: false })
  }
  const handleReject = (e: ReactMouseEvent) => {
    e.stopPropagation()
    nodeData.onSetCreativeCandidateState?.(id, { rejected: !rejected, selected: false })
  }
  const submitExpand = (instruction: string | null) => {
    setExpandPopoverOpen(false)
    setExpandInstruction('')
    nodeData.onExpandCreativeCandidate?.(id, instruction)
  }

  const rootBriefNodeId = (fields.rootBriefNodeId as string | null) ?? null
  const onDragStartCreativeContext = (e: ReactDragEvent) => {
    const payload: CreativeContextDragPayload = {
      sourceNodeId: id,
      sourceRunId: (fields.sourceRunId as string | null) ?? null,
      // Legacy `creative_candidate` keeps its Structured Context Assets
      // on a SEPARATE `project_start` root Brief node (spec §6) — never
      // on the candidate itself.
      assetsSourceNodeId: rootBriefNodeId,
      candidateId: String(fields.candidateId ?? id),
      title,
      premise,
      hook,
      tone,
      storyDirection: String(fields.storyDirection ?? ''),
      productRole,
      characterRole,
      estimatedDuration,
      tags,
      rationale: String(fields.rationale ?? ''),
    }
    e.dataTransfer.setData(CREATIVE_CONTEXT_DND_TYPE, JSON.stringify(payload))
    e.dataTransfer.effectAllowed = 'copy'
  }

  return (
    <div
      className={`pcv-node pcv-node--creative-candidate${selected ? ' is-selected' : ''}${
        selectedCandidate ? ' is-selected-candidate' : ''
      }${rejected ? ' is-rejected-candidate' : ''}${stale ? ' is-stale-candidate' : ''}`}
      style={{ width: NODE_WIDTH }}
      data-testid="canvas-creative-candidate-node"
    >
      <Handle type="target" position={Position.Left} className="pcv-handle" />

      <div
        className="pcv-candidate-header nodrag"
        {...vistoeraDndProps('creative-context')}
        draggable
        onDragStart={onDragStartCreativeContext}
        title="拖入「生成剧本」节点，作为剧本生成的创意上下文"
      >
        <span className="pcv-candidate-index" style={{ background: def.color }}>
          {generationDepth > 0 ? generationDepth + 1 : <ThunderboltOutlined style={{ fontSize: 9 }} />}
        </span>
        <span className="pcv-candidate-title" title={title || def.label}>
          {title || def.label}
        </span>
        <FileDoneOutlined className="pcv-candidate-drag-hint" title="可拖拽" />
        {selectedCandidate ? (
          <span className="pcv-candidate-selected-badge">
            <CheckOutlined /> 已选
          </span>
        ) : null}
      </div>

      <div className="pcv-candidate-body">
        {stale ? <div className="pcv-candidate-stale-banner">基于旧版本创意要求</div> : null}
        {premise ? <div className="pcv-candidate-premise">{premise}</div> : null}
        {hook ? <div className="pcv-candidate-premise" style={{ opacity: 0.75, fontStyle: 'italic' }}>“{hook}”</div> : null}
        <div className="pcv-candidate-meta-row">
          {tone ? <span className="pcv-candidate-tag">{tone}</span> : null}
          {durationLabel ? <span className="pcv-candidate-tag">⏱ {durationLabel}</span> : null}
          {sceneEstimateTag ? <span className="pcv-candidate-tag">🎬 {sceneEstimateTag}</span> : null}
          {productRole ? <span className="pcv-candidate-tag">产品：{productRole}</span> : null}
          {characterRole ? <span className="pcv-candidate-tag">角色：{characterRole}</span> : null}
        </div>
        {expandRunStatus === 'ERROR' && expandRunErrorMessage ? (
          <div className="pcv-gen-capability-warning pcv-gen-capability-warning--error">{expandRunErrorMessage}</div>
        ) : null}
      </div>

      <div className="pcv-candidate-actions nodrag">
        <button
          type="button"
          className={`pcv-candidate-action-btn${selectedCandidate ? ' pcv-candidate-action-btn--primary' : ''}`}
          onClick={handleSelect}
        >
          <CheckOutlined /> {selectedCandidate ? '已选择' : '选择此方案'}
        </button>
        <Tooltip title="对当前方案再生成 3 个子方向">
          <button
            type="button"
            ref={expandBtnRef}
            className="pcv-candidate-action-btn"
            disabled={expandBusy}
            onClick={(e) => {
              e.stopPropagation()
              setExpandPopoverOpen(true)
            }}
          >
            {expandBusy ? <LoadingOutlined spin /> : '💡'} {expandBusy ? '发散中…' : '发散更多'}
          </button>
        </Tooltip>
        <button type="button" className="pcv-candidate-action-btn pcv-candidate-action-btn--danger" onClick={handleReject}>
          <StopOutlined /> {rejected ? '已淘汰' : '淘汰'}
        </button>
        <Tooltip title="将标题栏拖拽到「生成剧本」节点上，作为其创意上下文">
          <button type="button" className="pcv-candidate-action-btn" disabled>
            拖拽继续 →
          </button>
        </Tooltip>
      </div>

      <Handle type="source" position={Position.Right} className="pcv-handle" />

      <CanvasActionPopover anchorEl={expandBtnRef.current} open={expandPopoverOpen} onClose={() => setExpandPopoverOpen(false)} title="发散 / 头脑风暴" width={240}>
        <div className="pcv-candidate-expand-popover">
          <span style={{ fontSize: 11, color: 'var(--pcv-text-tertiary)' }}>想往什么方向变化？（可选）</span>
          <textarea
            className="pcv-candidate-expand-input nodrag"
            placeholder="例如：更搞笑、更电影感、不要秘密基地……"
            value={expandInstruction}
            onChange={(e) => setExpandInstruction(e.target.value)}
          />
          <div className="pcv-candidate-expand-actions">
            <button type="button" className="pcv-candidate-action-btn" onClick={() => setExpandPopoverOpen(false)}>
              取消
            </button>
            <button
              type="button"
              className="pcv-candidate-action-btn pcv-candidate-action-btn--primary"
              onClick={() => submitExpand(expandInstruction.trim() || null)}
            >
              生成 3 个子方向
            </button>
          </div>
        </div>
      </CanvasActionPopover>
    </div>
  )
}

export default memo(CreativeCandidateNodeImpl)
